import React, { Suspense } from 'react'
import { Flex, Provider as ThemeProvider, styled, css } from '@chilipiper/design-system'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { rolloutInit } from '@chilipiper/rollout'
import { PersonalWorkspaceIdProvider } from '@chilipiper/scheduling-shared'
import { FireSessionProvider, useFireSessionContext } from '@chilipiper/fire-session'
import { ErrorBoundary as SentryErrorBoundary, withProfiler } from '@chilipiper/sentry'
import { LoaderAnimation, ToastProvider } from '@chilipiper/design-system/src/new'
import { QueryClient, QueryClientProvider, useSuspenseQuery } from '@tanstack/react-query'
import { AutoSaveStateProvider } from '@chilipiper/meeting-type-components'
import { useUserSlug } from '@chilipiper/workspace-queries/src/api-queries/fire-router'
import { usePersonalWorkspace } from '@chilipiper/workspace-queries/src/api-queries/workspace'
import { LocaleProvider } from '@chilipiper/locale/src/new'
import { TenantPicker, useTenantDetailsSuspense } from '@chilipiper/tenants'
import { useTracking, UserPosthogProvider } from '@chilipiper/tracking'
import { addFirePrefix } from '@chilipiper/admin-center-common'
import { LeftNav } from './components/left-nav/LeftNav'
import { GlobalStateProvider } from './context/global'
import { OnboardingRoutes } from './routes/OnboardingRoutes'
import { MailboxAllowedToast } from './components/personal-settings/integrations/MailboxIntegration/MailboxAllowedToast'
import { lazyLoading } from './utils/lazy-loading'
import '@chilipiper/design-system/css/reset.css'
import './reset.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import './components/calendar/calendar.css'
import { useAdoption } from './hooks/useAdoption'
import { useExtensionInstalledTracking, usePageviewTracking } from './hooks/useTracking'
import { useUser } from './api/workspace'
import { shouldRetryQuery } from './utils/query-retry'
import { SubAppProvider } from './SubApp'
import { ApiClientProvider } from './api/apiClientContext'
import { BrokenIntegrationsBanner } from './components/BrokenIntegrationsBanner'
import { NotConnectedCalendarBanner } from './components/NotConnectedCalendarBanner'
import { SentryUser } from './components/SentryUser'

const ChiliCalRoutes = lazyLoading(() => import('./routes/ChiliCalRoutes'))

const Container = styled(Flex)<{ isObjectNav?: boolean }>`
  background: ${props => props.theme.colors['bg/moderate']};
  overflow: auto;
  flex-grow: 1;
  ${props =>
    props.isObjectNav &&
    css`
      border-radius: 0;
      margin: 0;
      box-shadow: ${props => props.theme.shadows['border/section']};
    `}
`

const Main = () => {
  const { userId } = useFireSessionContext()

  const [{ data }, { data: userSlug, isError: isUserSlugError }] = parallelQueries([
    /* eslint-disable react-hooks/rules-of-hooks */
    () => useUser({ suspense: true }),
    () => useUserSlug({ suspense: true, userId }),
    /* eslint-enable react-hooks/rules-of-hooks */
  ] as const)

  useAdoption()
  useTracking()
  useExtensionInstalledTracking()
  usePageviewTracking()

  const isError = isUserSlugError
  const hasAllOnboardingData = Boolean(userSlug)

  const needOnboarding =
    !isError && (data?.onboardingStatus === 'Onboarding' || !hasAllOnboardingData)

  return (
    <Flex flexDirection='column' bg='bg/moderate' overflow='hidden' h='100%'>
      {needOnboarding ? (
        <OnboardingRoutes />
      ) : (
        <>
          <BrokenIntegrationsBanner />
          <NotConnectedCalendarBanner />
          <Flex bg='bg/moderate' overflow='hidden' h='100%'>
            <LeftNav />
            <Flex flex='1' overflow='hidden'>
              <Container data-test-id='content-container' w='100%'>
                <MailboxAllowedToast />
                <ChiliCalRoutes />
              </Container>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  )
}

const technicalBasePath = '/chilical'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 15000),
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
})

const TenantScopedProviders = ({ children }: { children: React.ReactNode }) => (
  <TenantPicker>
    <PersonalWorkspaceIdProvider>{children}</PersonalWorkspaceIdProvider>
  </TenantPicker>
)

// Fetches the session-only boot queries concurrently, so the providers below — which
// own them at nested depths and would each fetch in turn — hydrate from cache instead.
// Calls the same hooks they use, so the keys match by construction and can't drift.
const BootQueriesWarmup = ({ children }: { children: React.ReactNode }) => {
  const { tenantId, userId } = useFireSessionContext()

  parallelQueries([
    /* eslint-disable react-hooks/rules-of-hooks */
    // No options: this observer mounts first, so the client default governs the fetch.
    () => useTenantDetailsSuspense(tenantId),
    () => usePersonalWorkspace(userId, { suspense: true }),
    () => useUserSlug({ suspense: true, userId }),
    /* eslint-enable react-hooks/rules-of-hooks */
  ] as const)

  return <>{children}</>
}

const sharedProvidersRender = (children: React.ReactNode) => (
  <ApiClientProvider>
    <RolloutInitWithFireSession>
      {/* A rejected warm-up falls through to the original nesting, which fetches as before. */}
      <SentryErrorBoundary
        beforeCapture={scope => {
          scope.setTag('component', 'BootQueriesWarmup')
        }}
        fallback={<TenantScopedProviders>{children}</TenantScopedProviders>}
      >
        <BootQueriesWarmup>
          <TenantScopedProviders>{children}</TenantScopedProviders>
        </BootQueriesWarmup>
      </SentryErrorBoundary>
    </RolloutInitWithFireSession>
  </ApiClientProvider>
)

const SuspenseFallback = () => (
  <Flex w='100%' h='100%' justifyContent='center' alignItems='center'>
    <LoaderAnimation />
  </Flex>
)

export const App = withProfiler(() => {
  // We're using service name to internal routing, so we need to redirect to the correct base path
  if (window.location.pathname.startsWith(technicalBasePath)) {
    const url = new URL(window.location.toString())
    url.pathname = url.pathname.replace(technicalBasePath, addFirePrefix('/'))

    window.history.replaceState(undefined, '', url)
  }

  return (
    <SentryErrorBoundary>
      <AutoSaveStateProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <LocaleProvider>
            <GlobalStateProvider>
              <ThemeProvider>
                <ToastProvider>
                  <Suspense fallback={<SuspenseFallback />}>
                    <SubAppProvider sharedProvidersRender={sharedProvidersRender}>
                      <Main />
                    </SubAppProvider>
                  </Suspense>
                </ToastProvider>
              </ThemeProvider>
            </GlobalStateProvider>
          </LocaleProvider>
        </QueryClientProvider>
      </AutoSaveStateProvider>
    </SentryErrorBoundary>
  )
})

const RolloutInitWithFireSession = ({ children }: { children: React.ReactNode }) => {
  const { data: rolloutCallback } = useSuspenseQuery({
    queryKey: ['rollout-init'],
    queryFn: () => rolloutInit('my-app', { tier: 'authorized' }),
    gcTime: Infinity,
  })

  return (
    <FireSessionProvider onSuccess={rolloutCallback} suspense>
      <SentryUser />
      <UserPosthogProvider appName='chilical' appVersion={__APP_VERSION__}>
        {children}
      </UserPosthogProvider>
    </FireSessionProvider>
  )
}

const parallelQueries = <T extends ReadonlyArray<() => unknown>>(queries: T) => {
  const results: unknown[] = []
  const suspenseStack: Promise<unknown>[] = []
  for (const query of queries) {
    // try calling query, if it throws a promise (suspends), add to the stack
    try {
      const result = query()
      results.push(result)
    } catch (error) {
      if (error instanceof Promise) {
        suspenseStack.push(error)
      } else {
        throw error
      }
    }
  }
  if (suspenseStack.length) {
    // return a promise awaiting all the suspended queries
    throw Promise.all(suspenseStack)
  }
  return results as { [K in keyof T]: ReturnType<T[K]> }
}
